import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import env from '@eneco/common/env';
import { useExperimentsGetExperiments } from '@eneco/dc/hooks';
import { AuthenticationContext } from '@eneco/dc/provider';
import useDC from '@eneco/dc/useDC';
import { FlowActions, FlowRoute } from '@eneco/flows/types';
import { GrowthBook } from '@eneco/growthbook';
import { UseRouter, useRouter } from '@eneco/next';
import { useGTM } from '@eneco/tracking';
import { Attributes, GrowthBookProvider } from '@growthbook/growthbook-react';
import { Customers_Experiments_ExperimentResponseModel } from '@monorepo-types/dc';

import { createGrowthbookInstance } from './growthbook.helpers';
import { FlowConsumerContextEnergy } from '../../context';
import { MergedConfig, TestConfigVariant } from '../types/experiment';

/** Max time to wait for experiments API before rendering flow with default config (avoids blocking on CORS or slow API). */
const EXPERIMENTS_LOAD_TIMEOUT_MS = 5000;

interface ExperimentProviderProps {
  children: ReactNode;
  attributes?: Attributes;
  testConfigs?: TestConfigVariant[];
  defaultConfig: MergedConfig;
}

interface ExperimentFlowProviderProps {
  isLoading: boolean;
  children: ReactNode;
  routes: FlowRoute<FlowConsumerContextEnergy>[];
  actions: FlowActions<FlowConsumerContextEnergy>;
  initialContext: FlowConsumerContextEnergy;
}

interface ExtendedAttributes extends Attributes {
  disableExperiments?: boolean;
  forceTestIndex?: number;
}

const ExperimentFlowPContext = createContext<{
  isLoading: boolean;
  routes: FlowRoute<FlowConsumerContextEnergy>[];
  actions: FlowActions<FlowConsumerContextEnergy>;
  initialContext: FlowConsumerContextEnergy;
}>({
  isLoading: false,
  routes: [],
  actions: {},
  initialContext: {},
});

export const ExperimentFlowProvider: FC<ExperimentFlowProviderProps> = ({
  isLoading,
  actions,
  initialContext,
  routes,
  children,
}) => {
  const value = useMemo(() => {
    return { isLoading, routes, actions, initialContext };
  }, [isLoading, routes, actions, initialContext]);

  return <ExperimentFlowPContext.Provider value={value}>{children}</ExperimentFlowPContext.Provider>;
};

// Hook to use the loading state anywhere in the app
export const useExperimentFlowProvider = () => {
  return useContext(ExperimentFlowPContext);
};

export const useGrowthbookInstance = ({
  data,
  testConfigs,
  defaultConfig,
  router,
  dc,
  attributes,
  isLoading,
}: {
  data: Customers_Experiments_ExperimentResponseModel[] | null | undefined;
  testConfigs?: TestConfigVariant[];
  defaultConfig: MergedConfig;
  router: UseRouter;
  dc: AuthenticationContext;
  attributes?: ExtendedAttributes;
  isLoading: boolean;
}) => {
  const [instance, setInstance] = useState<GrowthbookInstanceState>(null);
  const [waitExpired, setWaitExpired] = useState(false);
  const initializedRef = useRef(false);
  const { pushToDataLayer } = useGTM();

  const configRef = useRef({ data, testConfigs, defaultConfig, router, dc, attributes, pushToDataLayer });
  configRef.current = { data, testConfigs, defaultConfig, router, dc, attributes, pushToDataLayer };

  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setWaitExpired(true), EXPERIMENTS_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isLoading]);

  const ready = !isLoading || waitExpired;

  useEffect(() => {
    if (!ready || initializedRef.current) return;
    initializedRef.current = true;

    const {
      data: d,
      testConfigs: tc,
      defaultConfig: dcCfg,
      router: r,
      dc: dcCtx,
      attributes: a,
      pushToDataLayer: p,
    } = configRef.current;

    setInstance(
      createGrowthbookInstance({
        data: d ?? null,
        testConfigs: tc,
        defaultConfig: dcCfg,
        router: r,
        dc: dcCtx,
        attributes: a,
        pushToDataLayer: p,
      }),
    );
  }, [ready]);

  return instance;
};

type GrowthbookInstanceState = {
  growthbook: GrowthBook;
  routes: FlowRoute<FlowConsumerContextEnergy>[];
  actions: FlowActions<FlowConsumerContextEnergy>;
  initialContext: FlowConsumerContextEnergy;
} | null;

export const ExperimentProvider = ({ children, defaultConfig, attributes, testConfigs }: ExperimentProviderProps) => {
  const router = useRouter();
  const dc = useDC();
  const growthBookProjectId = env('GROWTHBOOK_PROJECT_ID');

  const { data, isLoading } = useExperimentsGetExperiments({ projectId: growthBookProjectId }, { errorRetryCount: 0 });

  const growthbookInstance = useGrowthbookInstance({
    data,
    testConfigs,
    defaultConfig,
    router,
    dc,
    attributes,
    isLoading,
  });

  const updateGrowthBookURL = useCallback(() => {
    growthbookInstance?.growthbook?.setURL(window.location.href);
  }, [growthbookInstance?.growthbook]);

  useEffect(() => {
    router.events.on('routeChangeComplete', updateGrowthBookURL);
    return () => router.events.off('routeChangeComplete', updateGrowthBookURL);
  }, [router.events, updateGrowthBookURL]);

  if (!growthbookInstance) return null;

  return (
    <ExperimentFlowProvider
      isLoading={isLoading}
      routes={growthbookInstance.routes}
      initialContext={growthbookInstance.initialContext}
      actions={growthbookInstance.actions}>
      <GrowthBookProvider growthbook={growthbookInstance.growthbook}>{children}</GrowthBookProvider>
    </ExperimentFlowProvider>
  );
};
